import React, { useRef, useState } from "react";
import { toast } from 'react-toastify';
import { Trash2, Plus, Upload, Image as ImageIcon, Edit, Save, X, ChevronDown, ChevronUp,SquareCheckBigIcon } from 'lucide-react';

const ProjectionField = ({ sections, cards, setCards, setSections, openModal, handleSubmitAll }) => {
  const [errors, setErrors] = useState({});
  const fileInputRefs = useRef({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleCardChange = (section, cardId, field, value) => {
    setCards({
      ...cards,
      [section]: cards[section].map((card) =>
        card.id === cardId ? { ...card, [field]: value } : card
      ),
    });
  };

  const handleAddRow = (section) => {
    const newCard = { id: Date.now(), isEditing: true };
    
    // Add fields based on section type
    switch(section) {
      case 'History':
        newCard.title = "";
        newCard.content = "";
        newCard.imageUrl = "";
        break;
      case 'Transport':
        newCard.type = "";
        newCard.details = "";
        newCard.imageUrl = "";
        break;
      case 'Social Impact':
        newCard.aspect = "";
        newCard.impact = "";
        newCard.imageUrl = "";
        break;
      case 'Population':
        newCard.year = "";
        newCard.count = "";
        newCard.notes = "";
        newCard.imageUrl = "";
        break;
      case 'Culture':
        newCard.aspect = "";
        newCard.details = "";
        newCard.imageUrl = "";
        break;
      case 'Economy':
        newCard.sector = "";
        newCard.info = "";
        newCard.imageUrl = "";
        break;
      case 'Technology':
        newCard.field = "";
        newCard.description = "";
        newCard.imageUrl = "";
        break;
      case 'Public':
        newCard.field = "";
        newCard.description = "";
        newCard.imageUrl = "";
        break;
      default:
        break;
    }

    setCards({
      ...cards,
      [section]: [
        ...(cards[section] || []),
        newCard
      ]
    });
    setExpandedSections(prev => ({ ...prev, [section]: true }));
  };

  const validateCard = (card, section) => {
    const newErrors = {};
    switch(section) {
      case 'History':
        if (!card.title) newErrors.title = "Title is required";
        if (!card.content) newErrors.content = "Content is required";
        break;
      case 'Transport':
        if (!card.type) newErrors.type = "Type is required";
        if (!card.details) newErrors.details = "Details are required";
        break;
      case 'Social Impact':
        if (!card.aspect) newErrors.aspect = "Aspect is required";
        if (!card.impact) newErrors.impact = "Impact is required";
        break;
      case 'Population':
        if (!card.year) newErrors.year = "Year is required";
        if (!card.count) newErrors.count = "Count is required";
        break;
      case 'Culture':
        if (!card.aspect) newErrors.aspect = "Aspect is required";
        if (!card.details) newErrors.details = "Details are required";
        break;
      case 'Economy':
        if (!card.sector) newErrors.sector = "Sector is required";
        if (!card.info) newErrors.info = "Info is required";
        break;
      case 'Technology':
        if (!card.field) newErrors.field = "Field is required";
        if (!card.description) newErrors.description = "Description is required";
        break;
        case 'Public':
          if(!card.field)newErrors.field = "Field is required";
          if(!card.description)newErrors.description = "Description is required";
          break;
      default:
        break;
    }
    return newErrors;
  };

  const handleDeleteRow = (section, cardId) => {
    setCardToDelete({ section, cardId });
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    const { section, cardId } = cardToDelete;
    const updatedCards = {
      ...cards,
      [section]: cards[section].filter((card) => card.id !== cardId),
    };

    setCards(updatedCards);

    if (updatedCards[section].length === 0) {
      setSections(sections.filter((sec) => sec !== section));
      const { [section]: _, ...remainingCards } = updatedCards;
      setCards(remainingCards);
    }

    toast.success('Row deleted successfully!');
    setShowDeleteModal(false);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setCardToDelete(null);
  };

  const handleImageUpload = (section, cardId, e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match('image.*')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size should be less than 2MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        handleCardChange(section, cardId, "imageUrl", reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditCard = (section, cardId) => {
    setCards({
      ...cards,
      [section]: cards[section].map((card) =>
        card.id === cardId ? { ...card, isEditing: !card.isEditing } : card
      ),
    });
  };

  const renderFieldInput = (section, card, cardId, fieldName, placeholder, required = true) => {
    return (
      <div className="relative">
        <input
          type="text"
          value={card[fieldName] || ""}
          onChange={(e) => handleCardChange(section, cardId, fieldName, e.target.value)}
          className={`w-full p-2 rounded-lg border ${errors[cardId]?.[fieldName] ? 'border-red-500/50 focus:ring-red-500/30' : 'border-gray-600/50 focus:ring-indigo-500/30'} bg-gray-700/20 text-white focus:outline-none focus:ring-2 focus:border-transparent transition-all ${!card.isEditing ? 'opacity-80 cursor-not-allowed' : ''}`}
          placeholder={placeholder}
          readOnly={!card.isEditing}
          required={required}
        />
        {errors[cardId]?.[fieldName] && (
          <p className="absolute -bottom-5 left-0 text-red-400 text-xs mt-1">{errors[cardId][fieldName]}</p>
        )}
      </div>
    );
  };

  const renderTextareaInput = (section, card, cardId, fieldName, placeholder, required = true) => {
    return (
      <div className="relative">
        <textarea
          value={card[fieldName] || ""}
          onChange={(e) => handleCardChange(section, cardId, fieldName, e.target.value)}
          className={`w-full p-2 rounded-lg border ${errors[cardId]?.[fieldName] ? 'border-red-500/50 focus:ring-red-500/30' : 'border-gray-600/50 focus:ring-indigo-500/30'} bg-gray-700/20 text-white focus:outline-none focus:ring-2 focus:border-transparent transition-all ${!card.isEditing ? 'opacity-80 cursor-not-allowed' : ''}`}
          placeholder={placeholder}
          rows="2"
          readOnly={!card.isEditing}
          required={required}
        />
        {errors[cardId]?.[fieldName] && (
          <p className="absolute -bottom-5 left-0 text-red-400 text-xs mt-1">{errors[cardId][fieldName]}</p>
        )}
      </div>
    );
  };

  const renderSectionFields = (section, card, cardId) => {
    switch(section) {
      case 'History':
        return (
          <>
            <td className="px-6 py-4 whitespace-nowrap">
              {renderFieldInput(section, card, cardId, 'title', 'Title (e.g., Nawabi Era)')}
            </td>
            <td className="px-6 py-4">
              {renderTextareaInput(section, card, cardId, 'content', 'Historical content')}
            </td>
          </>
        );
      case 'Transport':
        return (
          <>
            <td className="px-6 py-4 whitespace-nowrap">
              {renderFieldInput(section, card, cardId, 'type', 'Type (e.g., Metro, Rail)')}
            </td>
            <td className="px-6 py-4">
              {renderTextareaInput(section, card, cardId, 'details', 'Transport details')}
            </td>
          </>
        );
      case 'Social Impact':
        return (
          <>
            <td className="px-6 py-4 whitespace-nowrap">
              {renderFieldInput(section, card, cardId, 'aspect', 'Aspect (e.g., Education)')}
            </td>
            <td className="px-6 py-4">
              {renderTextareaInput(section, card, cardId, 'impact', 'Social impact')}
            </td>
          </>
        );
      case 'Population':
        return (
          <>
            <td className="px-6 py-4 whitespace-nowrap">
              {renderFieldInput(section, card, cardId, 'year', 'Year (e.g., 2021)')}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              {renderFieldInput(section, card, cardId, 'count', 'Population count')}
            </td>
            <td className="px-6 py-4">
              {renderTextareaInput(section, card, cardId, 'notes', 'Additional notes', false)}
            </td>
          </>
        );
      case 'Culture':
        return (
          <>
            <td className="px-6 py-4 whitespace-nowrap">
              {renderFieldInput(section, card, cardId, 'aspect', 'Aspect (e.g., Festivals)')}
            </td>
            <td className="px-6 py-4">
              {renderTextareaInput(section, card, cardId, 'details', 'Cultural details')}
            </td>
          </>
        );
      case 'Economy':
        return (
          <>
            <td className="px-6 py-4 whitespace-nowrap">
              {renderFieldInput(section, card, cardId, 'sector', 'Sector (e.g., IT and Startups)')}
            </td>
            <td className="px-6 py-4">
              {renderTextareaInput(section, card, cardId, 'info', 'Economic information')}
            </td>
          </>
        );
      case 'Technology':
        return (
          <>
            <td className="px-6 py-4 whitespace-nowrap">
              {renderFieldInput(section, card, cardId, 'field', 'Field (e.g., Smart City Projects)')}
            </td>
            <td className="px-6 py-4">
              {renderTextareaInput(section, card, cardId, 'description', 'Technology description')}
            </td>
          </>
        );
      case 'Infrastructure':
        return (
          <>
            <td className="px-6 py-4 whitespace-nowrap">
              {renderFieldInput(section, card, cardId, 'field', 'Field (e.g., Smart City Projects)')}
            </td>
            <td className="px-6 py-4">
              {renderTextareaInput(section, card, cardId, 'description', 'Infrastructure description')}
            </td>
          </>
        );
      default:
        return null;
    }
  };

  const renderSectionHeaders = (section) => {
    switch(section) {
      case 'History':
        return (
          <>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Title</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Content</th>
          </>
        );
      case 'Transport':
        return (
          <>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Type</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Details</th>
          </>
        );
      case 'Social Impact':
        return (
          <>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Aspect</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Impact</th>
          </>
        );
      case 'Population':
        return (
          <>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Year</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Count</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Notes</th>
          </>
        );
      case 'Culture':
        return (
          <>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Aspect</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Details</th>
          </>
        );
      case 'Economy':
        return (
          <>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Sector</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Info</th>
          </>
        );
      case 'Technology':
        return (
          <>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Field</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Description</th>
          </>
        );
      case 'Infrastructure':
        return (
          <>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Field</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Description</th>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-6">
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md border border-indigo-500/30 shadow-2xl shadow-indigo-500/10">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Confirm Deletion</h3>
              <button 
                onClick={cancelDelete}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete this row? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 transition-all border border-gray-600 hover:border-gray-500 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-500 transition-all flex items-center border border-red-700 hover:border-red-600 font-medium shadow-lg shadow-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
           <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white bg-gradient-to-r from-indigo-400 to-purple-500 bg-clip-text text-transparent">
            Projection Field
          </h1>
          <button
            onClick={openModal}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white px-6 py-3 rounded-xl shadow-lg flex items-center transition-all hover:shadow-xl font-medium group"
          >
            <Plus className="w-5 h-5 mr-2 transition-transform group-hover:rotate-90" />
            Add New Section
          </button>
        </div>
        {sections.length > 0 ? (
          <div className="space-y-6">
            {sections.map((section) => (
              <div key={section} className="bg-gray-800/50 rounded-xl border border-gray-700/50 shadow-lg overflow-hidden backdrop-blur-sm">
                <div 
                  className="p-5 border-b border-gray-700/50 flex justify-between items-center cursor-pointer hover:bg-gray-700/30 transition-colors"
                  onClick={() => toggleSection(section)}
                >
                  <div className="flex items-center space-x-3">
                    <h2 className="text-xl font-bold text-white flex items-center">
                      <span className="bg-indigo-500/10 text-indigo-400 px-3 py-1 rounded-lg mr-3">
                        {section}
                      </span>
                    </h2>
                    <span className="text-sm text-gray-400 bg-gray-700 px-2 py-1 rounded">
                      {cards[section]?.length || 0} {cards[section]?.length === 1 ? 'item' : 'items'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddRow(section);
                      }}
                      className="bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-400 px-4 py-2 rounded-lg flex items-center transition-all"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Row
                    </button>
                    {expandedSections[section] ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {expandedSections[section] && (
                  <div className="overflow-x-auto transition-all duration-300">
                    <table className="min-w-full divide-y divide-gray-700/50">
                      <thead className="bg-gray-700/30">
                        <tr>
                          {renderSectionHeaders(section)}
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Image</th>
                          <th className="px-6 py-4 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700/30">
                        {(cards[section] || []).map((card) => (
                          <tr key={card.id} className="hover:bg-gray-700/10 transition-colors">
                            {renderSectionFields(section, card, card.id)}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="file"
                                accept="image/*"
                                ref={el => fileInputRefs.current[`${section}-${card.id}`] = el}
                                onChange={(e) => handleImageUpload(section, card.id, e)}
                                className="hidden"
                                id={`image-upload-${section}-${card.id}`}
                              />
                              <div className="flex items-center space-x-2 relative">
                                {card.imageUrl ? (
                                  <div className="relative group">
                                    <img 
                                      src={card.imageUrl} 
                                      alt="Preview" 
                                      className="h-12 w-12 object-cover rounded-lg cursor-pointer border border-gray-600/50 hover:border-indigo-400 transition-all"
                                      onClick={() => window.open(card.imageUrl, '_blank')}
                                    />
                                    {card.isEditing && (
                                      <button
                                        onClick={() => document.getElementById(`image-upload-${section}-${card.id}`).click()}
                                        className="absolute -top-2 -right-2 bg-indigo-600 hover:bg-indigo-500 text-white p-1 rounded-full shadow-md transition-all"
                                        title="Change image"
                                      >
                                        <Edit className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => document.getElementById(`image-upload-${section}-${card.id}`).click()}
                                    className={`bg-gray-700/50 hover:bg-gray-700/70 text-white px-3 py-2 rounded-lg text-sm border border-gray-600/50 flex items-center transition-all ${!card.isEditing ? 'opacity-70 cursor-not-allowed' : 'hover:border-indigo-400'}`}
                                    disabled={!card.isEditing}
                                  >
                                    <Upload className="w-4 h-4 mr-1" />
                                    Upload
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => handleDeleteRow(section, card.id)}
                                  className="p-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 hover:text-red-300 rounded-lg transition-all border border-red-600/30 hover:border-red-500/50"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleEditCard(section, card.id)}
                                  className={`p-2 ${card.isEditing ? 'bg-green-600/20 hover:bg-green-600/30 text-green-400 hover:text-green-300 border-green-600/30 hover:border-green-500/50' : 'bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-400 hover:text-indigo-300 border-indigo-600/30 hover:border-indigo-500/50'} rounded-lg transition-all border`}
                                  title={card.isEditing ? "Save" : "Edit"}
                                >
                                  {card.isEditing ? <Save className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
        <div className="text-center py-20 bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-700/50 mt-10">
  <div className="max-w-md mx-auto">
    <div className="flex justify-center mb-6 relative group">
      {/* Icon button */}
      <div className="bg-indigo-500/10 p-5 rounded-full cursor-pointer relative z-10">
        <Plus className="w-10 h-10 text-indigo-400" onClick={openModal} />
      </div>

      {/* Tooltip */}
                <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm px-3 py-1.5 rounded-md shadow-lg opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 transition-all duration-200 pointer-events-none">
                  Add a new section
                  <div className="absolute bottom-[-6px] left-1/2 -translate-x-1/2 w-3 h-3 bg-gray-900 rotate-45"></div>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-white mb-2">No sections added yet</h3>
              <p className="text-gray-400 mb-6">Get started by creating your first section</p>
                  </div>
              </div>

        )}

        {sections.length > 0 && (
          <button
            onClick={handleSubmitAll}
            className="fixed bottom-8 right-8 px-6 py-3 rounded-xl shadow-lg flex items-center transition-all hover:shadow-xl font-medium bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
          >
            <SquareCheckBigIcon className="w-5 h-5 mr-2" />
            Submit All Data
          </button>
        )}
      </div>
    </div>
  );
};

export default ProjectionField;
// import React, { useRef, useState } from "react";
// import { toast } from 'react-toastify';
// import { Trash2, Plus, Upload, Image as ImageIcon, Edit, Save, X, ChevronDown, ChevronUp } from 'lucide-react';

// const ProjectionField = ({ sections, cards, setCards, setSections, openModal }) => {
//   const [errors, setErrors] = useState({});
//   const fileInputRefs = useRef({});
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [cardToDelete, setCardToDelete] = useState(null);
//   const [submitted, setSubmitted] = useState(false);
//   const [expandedSections, setExpandedSections] = useState({});
//   const [hasEdits, setHasEdits] = useState(false);

//   const toggleSection = (section) => {
//     setExpandedSections(prev => ({
//       ...prev,
//       [section]: !prev[section]
//     }));
//   };

//   const handleCardChange = (section, cardId, field, value) => {
//     setCards({
//       ...cards,
//       [section]: cards[section].map((card) =>
//         card.id === cardId ? { ...card, [field]: value } : card
//       ),
//     });
//     if (submitted) {
//       setHasEdits(true);
//     }
//   };

//   const handleAddRow = (section) => {
//     setCards({
//       ...cards,
//       [section]: [
//         ...cards[section],
//         {
//           id: Date.now(),
//           name: "",
//           state: "",
//           country: "",
//           city: "",
//           image: null,
//           description: "",
//           isEditing: false
//         }
//       ]
//     });
//     setExpandedSections(prev => ({ ...prev, [section]: true }));
//     if (submitted) {
//       setHasEdits(true);
//     }
//   };

//   const validateCard = (card) => {
//     const newErrors = {};
//     if (!card?.name?.trim()) newErrors.name = "Name is required";
//     if (!card?.country?.trim()) newErrors.country = "Country is required";
//     if (!card?.state?.trim()) newErrors.state = "State is required";
//     if (!card?.city?.trim()) newErrors.city = "City is required";
//     if (!card?.description?.trim()) newErrors.description = "Description is required";
//     return newErrors;
//   };

//   const handleDeleteRow = (section, cardId) => {
//     setCardToDelete({ section, cardId });
//     setShowDeleteModal(true);
//     if (submitted) {
//       setHasEdits(true);
//     }
//   };

//   const confirmDelete = () => {
//     const { section, cardId } = cardToDelete;
//     const updatedCards = {
//       ...cards,
//       [section]: cards[section].filter((card) => card.id !== cardId),
//     };

//     setCards(updatedCards);

//     if (updatedCards[section].length === 0) {
//       setSections(sections.filter((sec) => sec !== section));
//       const { [section]: _, ...remainingCards } = updatedCards;
//       setCards(remainingCards);
//     }

//     toast.success('Row deleted successfully!');
//     setShowDeleteModal(false);
//   };

//   const cancelDelete = () => {
//     setShowDeleteModal(false);
//     setCardToDelete(null);
//   };

//   const handleImageUpload = (section, cardId, e) => {
//     const file = e.target.files[0];
//     if (file) {
//       if (!file.type.match('image.*')) {
//         toast.error('Please select an image file');
//         return;
//       }
//       if (file.size > 2 * 1024 * 1024) {
//         toast.error('Image size should be less than 2MB');
//         return;
//       }

//       const reader = new FileReader();
//       reader.onload = () => {
//         handleCardChange(section, cardId, "image", reader.result);
//       };
//       reader.readAsDataURL(file);
//     }
//   };

//   const handleEditCard = (section, cardId) => {
//     setCards({
//       ...cards,
//       [section]: cards[section].map((card) =>
//         card.id === cardId ? { ...card, isEditing: !card.isEditing } : card
//       ),
//     });
//     setHasEdits(true);
//   };

//   const handleSubmitAll = () => {
//     let hasErrors = false;
//     const newErrors = {};
//     const updatedCards = { ...cards };

//     sections.forEach(section => {
//       updatedCards[section] = cards[section].map(card => {
//         const cardErrors = validateCard(card);
//         if (Object.keys(cardErrors).length > 0) {
//           newErrors[card.id] = cardErrors;
//           hasErrors = true;
//         }
        
//         if (card.isEditing) {
//           return { ...card, isEditing: false };
//         }
//         return card;
//       });
//     });

//     if (hasErrors) {
//       setErrors(newErrors);
//       toast.error('Please fix all errors before submitting');
//       setTimeout(() => setErrors({}), 2000);
//       return;
//     }

//     setCards(updatedCards);
//     console.log("Submitted data:", updatedCards);
//     toast.success(hasEdits ? 'Data updated successfully!' : 'Data submitted successfully!');
//     setSubmitted(true);
//     setHasEdits(false);
//   };

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-6">
//       {showDeleteModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-50">
//           <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md border border-indigo-500/30 shadow-2xl shadow-indigo-500/10">
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-bold text-white">Confirm Deletion</h3>
//               <button 
//                 onClick={cancelDelete}
//                 className="text-gray-400 hover:text-white transition-colors"
//               >
//                 <X className="w-5 h-5" />
//               </button>
//             </div>
//             <p className="text-gray-300 mb-6">
//               Are you sure you want to delete this row? This action cannot be undone.
//             </p>
//             <div className="flex justify-end space-x-3">
//               <button
//                 onClick={cancelDelete}
//                 className="px-4 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 transition-all border border-gray-600 hover:border-gray-500 font-medium"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={confirmDelete}
//                 className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-500 transition-all flex items-center border border-red-700 hover:border-red-600 font-medium shadow-lg shadow-red-500/10"
//               >
//                 <Trash2 className="w-4 h-4 mr-2" />
//                 Delete
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <div className="max-w-7xl mx-auto">
//         <div className="flex justify-between items-center mb-8">
//           <h1 className="text-3xl font-bold text-white bg-gradient-to-r from-indigo-400 to-purple-500 bg-clip-text text-transparent">
//             Projection Fields
//           </h1>
//           <button
//             onClick={openModal}
//             className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white px-6 py-3 rounded-xl shadow-lg flex items-center transition-all hover:shadow-xl font-medium group"
//           >
//             <Plus className="w-5 h-5 mr-2 transition-transform group-hover:rotate-90" />
//             Add New Section
//           </button>
//         </div>

//         {sections.length > 0 ? (
//           <div className="space-y-6">
//             {sections.map((section) => (
//               <div key={section} className="bg-gray-800/50 rounded-xl border border-gray-700/50 shadow-lg overflow-hidden backdrop-blur-sm">
//                 <div 
//                   className="p-5 border-b border-gray-700/50 flex justify-between items-center cursor-pointer hover:bg-gray-700/30 transition-colors"
//                   onClick={() => toggleSection(section)}
//                 >
//                   <div className="flex items-center space-x-3">
//                     <h2 className="text-xl font-bold text-white flex items-center">
//                       <span className="bg-indigo-500/10 text-indigo-400 px-3 py-1 rounded-lg mr-3">
//                         {section}
//                       </span>
//                     </h2>
//                     <span className="text-sm text-gray-400 bg-gray-700 px-2 py-1 rounded">
//                       {cards[section].length} {cards[section].length === 1 ? 'item' : 'items'}
//                     </span>
//                   </div>
//                   <div className="flex items-center space-x-3">
//                     <button
//                       onClick={(e) => {
//                         e.stopPropagation();
//                         handleAddRow(section);
//                       }}
//                       className="bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-400 px-4 py-2 rounded-lg flex items-center transition-all"
//                     >
//                       <Plus className="w-4 h-4 mr-2" />
//                       Add Row
//                     </button>
//                     {expandedSections[section] ? (
//                       <ChevronUp className="w-5 h-5 text-gray-400" />
//                     ) : (
//                       <ChevronDown className="w-5 h-5 text-gray-400" />
//                     )}
//                   </div>
//                 </div>

//                 {expandedSections[section] && (
//                   <div className="overflow-x-auto transition-all duration-300">
//                     <table className="min-w-full divide-y divide-gray-700/50">
//                       <thead className="bg-gray-700/30">
//                         <tr>
//                           <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
//                           <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Country</th>
//                           <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">State</th>
//                           <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">City</th>
//                           <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Image</th>
//                           <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Description</th>
//                           <th className="px-6 py-4 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
//                         </tr>
//                       </thead>
//                       <tbody className="divide-y divide-gray-700/30">
//                         {cards[section].map((card) => (
//                           <tr key={card.id} className="hover:bg-gray-700/10 transition-colors">
//                             <td className="px-6 py-4 whitespace-nowrap">
//                               <div className="relative">
//                                 <input
//                                   type="text"
//                                   value={card.name}
//                                   onChange={(e) => handleCardChange(section, card.id, "name", e.target.value)}
//                                   className={`w-full p-2 rounded-lg border ${errors[card.id]?.name ? 'border-red-500/50 focus:ring-red-500/30' : 'border-gray-600/50 focus:ring-indigo-500/30'} bg-gray-700/20 text-white focus:outline-none focus:ring-2 focus:border-transparent transition-all ${submitted && !card.isEditing ? 'opacity-80 cursor-not-allowed' : ''}`}
//                                   readOnly={submitted && !card.isEditing}
//                                 />
//                                 {errors[card.id]?.name && (
//                                   <p className="absolute -bottom-5 left-0 text-red-400 text-xs mt-1">{errors[card.id].name}</p>
//                                 )}
//                               </div>
//                             </td>
//                             <td className="px-6 py-4 whitespace-nowrap">
//                               <div className="relative">
//                                 <input
//                                   type="text"
//                                   value={card.country}
//                                   onChange={(e) => handleCardChange(section, card.id, "country", e.target.value)}
//                                   className={`w-full p-2 rounded-lg border ${errors[card.id]?.country ? 'border-red-500/50 focus:ring-red-500/30' : 'border-gray-600/50 focus:ring-indigo-500/30'} bg-gray-700/20 text-white focus:outline-none focus:ring-2 focus:border-transparent transition-all ${submitted && !card.isEditing ? 'opacity-80 cursor-not-allowed' : ''}`}
//                                   readOnly={submitted && !card.isEditing}
//                                 />
//                                 {errors[card.id]?.country && (
//                                   <p className="absolute -bottom-5 left-0 text-red-400 text-xs mt-1">{errors[card.id].country}</p>
//                                 )}
//                               </div>
//                             </td>
//                             <td className="px-6 py-4 whitespace-nowrap">
//                               <div className="relative">
//                                 <input
//                                   type="text"
//                                   value={card.state}
//                                   onChange={(e) => handleCardChange(section, card.id, "state", e.target.value)}
//                                   className={`w-full p-2 rounded-lg border ${errors[card.id]?.state ? 'border-red-500/50 focus:ring-red-500/30' : 'border-gray-600/50 focus:ring-indigo-500/30'} bg-gray-700/20 text-white focus:outline-none focus:ring-2 focus:border-transparent transition-all ${submitted && !card.isEditing ? 'opacity-80 cursor-not-allowed' : ''}`}
//                                   readOnly={submitted && !card.isEditing}
//                                 />
//                                 {errors[card.id]?.state && (
//                                   <p className="absolute -bottom-5 left-0 text-red-400 text-xs mt-1">{errors[card.id].state}</p>
//                                 )}
//                               </div>
//                             </td>
//                             <td className="px-6 py-4 whitespace-nowrap">
//                               <div className="relative">
//                                 <input
//                                   type="text"
//                                   value={card.city}
//                                   onChange={(e) => handleCardChange(section, card.id, "city", e.target.value)}
//                                   className={`w-full p-2 rounded-lg border ${errors[card.id]?.city ? 'border-red-500/50 focus:ring-red-500/30' : 'border-gray-600/50 focus:ring-indigo-500/30'} bg-gray-700/20 text-white focus:outline-none focus:ring-2 focus:border-transparent transition-all ${submitted && !card.isEditing ? 'opacity-80 cursor-not-allowed' : ''}`}
//                                   readOnly={submitted && !card.isEditing}
//                                 />
//                                 {errors[card.id]?.city && (
//                                   <p className="absolute -bottom-5 left-0 text-red-400 text-xs mt-1">{errors[card.id].city}</p>
//                                 )}
//                               </div>
//                             </td>
//                             <td className="px-6 py-4 whitespace-nowrap">
//                               <input
//                                 type="file"
//                                 accept="image/*"
//                                 ref={el => fileInputRefs.current[`${section}-${card.id}`] = el}
//                                 onChange={(e) => handleImageUpload(section, card.id, e)}
//                                 className="hidden"
//                                 id={`image-upload-${section}-${card.id}`}
//                               />
//                               <div className="flex items-center space-x-2 relative">
//                                 {card.image ? (
//                                   <div className="relative group">
//                                     <img 
//                                       src={card.image} 
//                                       alt="Preview" 
//                                       className="h-12 w-12 object-cover rounded-lg cursor-pointer border border-gray-600/50 hover:border-indigo-400 transition-all"
//                                       onClick={() => window.open(card.image, '_blank')}
//                                     />
//                                     {(card.isEditing) && (
//                                       <button
//                                         onClick={() => document.getElementById(`image-upload-${section}-${card.id}`).click()}
//                                         className="absolute -top-2 -right-2 bg-indigo-600 hover:bg-indigo-500 text-white p-1 rounded-full shadow-md transition-all"
//                                         title="Change image"
//                                       >
//                                         <Edit className="w-3 h-3" />
//                                       </button>
//                                     )}
//                                   </div>
//                                 ) : (
//                                   <button
//                                     onClick={() => document.getElementById(`image-upload-${section}-${card.id}`).click()}
//                                     className={`bg-gray-700/50 hover:bg-gray-700/70 text-white px-3 py-2 rounded-lg text-sm border border-gray-600/50 flex items-center transition-all ${submitted && !card.isEditing ? 'opacity-70 cursor-not-allowed' : 'hover:border-indigo-400'}`}
//                                     disabled={submitted && !card.isEditing}
//                                   >
//                                     <Upload className="w-4 h-4 mr-1" />
//                                     Upload
//                                   </button>
//                                 )}
//                               </div>
//                             </td>
//                             <td className="px-6 py-4">
//                               <div className="relative">
//                                 <textarea
//                                   value={card.description}
//                                   onChange={(e) => handleCardChange(section, card.id, "description", e.target.value)}
//                                   className={`w-full p-2 rounded-lg border ${errors[card.id]?.description ? 'border-red-500/50 focus:ring-red-500/30' : 'border-gray-600/50 focus:ring-indigo-500/30'} bg-gray-700/20 text-white focus:outline-none focus:ring-2 focus:border-transparent transition-all ${submitted && !card.isEditing ? 'opacity-80 cursor-not-allowed' : ''}`}
//                                   rows="2"
//                                   readOnly={submitted && !card.isEditing}
//                                 />
//                                 {errors[card.id]?.description && (
//                                   <p className="absolute -bottom-5 left-0 text-red-400 text-xs mt-1">{errors[card.id].description}</p>
//                                 )}
//                               </div>
//                             </td>
//                             <td className="px-6 py-4 whitespace-nowrap text-right">
//                               <div className="flex justify-end space-x-2">
//                                 <button
//                                   onClick={() => handleDeleteRow(section, card.id)}
//                                   className="p-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 hover:text-red-300 rounded-lg transition-all border border-red-600/30 hover:border-red-500/50"
//                                   title="Delete"
//                                 >
//                                   <Trash2 className="w-4 h-4" />
//                                 </button>
//                                 {submitted && (
//                                   <button
//                                     onClick={() => handleEditCard(section, card.id)}
//                                     className={`p-2 ${card.isEditing ? 'bg-green-600/20 hover:bg-green-600/30 text-green-400 hover:text-green-300 border-green-600/30 hover:border-green-500/50' : 'bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-400 hover:text-indigo-300 border-indigo-600/30 hover:border-indigo-500/50'} rounded-lg transition-all border`}
//                                     title={card.isEditing ? "Save" : "Edit"}
//                                   >
//                                     {card.isEditing ? <Save className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
//                                   </button>
//                                 )}
//                               </div>
//                             </td>
//                           </tr>
//                         ))}
//                       </tbody>
//                     </table>
//                   </div>
//                 )}
//               </div>
//             ))}
//           </div>
//         ) : (
//           <div className="text-center py-20 bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-700/50 mt-10">
//             <div className="max-w-md mx-auto">
//               <div className="flex justify-center mb-6">
//                 <div className="bg-indigo-500/10 p-5 rounded-full">
//                   <Plus className="w-10 h-10 text-indigo-400" />
//                 </div>
//               </div>
//               <h3 className="text-2xl font-bold text-white mb-2">No sections added yet</h3>
//               <p className="text-gray-400 mb-6">Get started by creating your first section</p>
//             </div>
//           </div>
//         )}

//         {sections.length > 0 && (
//           <button
//             onClick={handleSubmitAll}
//             className={`fixed bottom-8 right-8 px-6 py-3 rounded-xl shadow-lg flex items-center transition-all hover:shadow-xl font-medium ${submitted ? (hasEdits ? 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white' : 'bg-green-600/90 text-white cursor-not-allowed') : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white'}`}
//             disabled={submitted && !hasEdits}
//           >
//             {submitted ? (
//               hasEdits ? (
//                 <>
//                   <Save className="w-5 h-5 mr-2" />
//                   Submit Updated Data
//                 </>
//               ) : (
//               <>
//                 <Save className="w-5 h-5 mr-2" />
//                 Submit All Data
//               </>
//             )
//             ) : (
//               <>
//                 <Save className="w-5 h-5 mr-2" />
//                 Submit All Data
//               </>
//             )}
//           </button>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ProjectionField;